import './ServiceOrderLoadedItems.scss';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Empty, Flex, Skeleton, Table, Tag, Tooltip } from 'antd';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import {
  SHIPPING_ITEM_REFERENCE,
  ShippingItemReferenceString,
} from '@/model/Logistic/Shipment/ShipmentReference';
import {
  SERVICE_ORDER_ITEM_RESOLUTION,
  ServiceOrderItem,
} from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ServiceOrderLoadedItemsProps {
  serviceOrderId: number;
  serviceOrderItems: ServiceOrderItem[];
  // Permite updates funcionales para evitar sobreescrituras.
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
}

const ServiceOrderLoadedItems = (props: ServiceOrderLoadedItemsProps) => {
  const { serviceOrderRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const { serviceOrderId, serviceOrderItems, setServiceOrderItems } = props;
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const items = useMemo(
    () =>
      serviceOrderItems
        .filter((item): item is ServiceOrderItem => item !== null && item !== undefined)
        .sort((a, b) => {
          // Prioridad 1: los items sin uso quedan al final.
          const hasNoUseA = (a.used_amount ?? 0) === 0;
          const hasNoUseB = (b.used_amount ?? 0) === 0;

          if (hasNoUseA !== hasNoUseB) {
            return hasNoUseA ? 1 : -1;
          }

          // Prioridad 2: primero items que provienen de envío.
          const isShipmentA = a.package_item !== null && a.package_item !== undefined;
          const isShipmentB = b.package_item !== null && b.package_item !== undefined;

          if (isShipmentA !== isShipmentB) {
            return isShipmentA ? -1 : 1;
          }

          // Prioridad 3: mantener orden por mayor cantidad utilizada.
          return (b.used_amount ?? 0) - (a.used_amount ?? 0);
        }),
    [serviceOrderItems],
  );

  useEffect(() => {
    let isActive = true;

    serviceOrderRepository
      .getServiceOrderItems(serviceOrderId)
      .then(items => {
        if (!isActive) {
          return;
        }

        setServiceOrderItems(items);
        setItemsLoaded(true);
      })
      .catch(error => {
        console.error('Error fetching service order items:', error);
      });

    return () => {
      isActive = false;
    };
  }, [serviceOrderId, serviceOrderRepository, setServiceOrderItems]);

  return (
    <div className="serviceOrderLoadedItems">
      {itemsLoaded ? (
        <Table<ServiceOrderItem>
          className="serviceOrderLoadedItemsTable"
          rowKey={item =>
            item.id ??
            item.name ??
            item.serial_number ??
            `service-order-item-${String(item.id ?? '')}-${String(item.name ?? '')}-${String(item.serial_number ?? '')}`
          }
          dataSource={items}
          pagination={false}
          size="small"
          tableLayout="fixed"
          scroll={{ x: 720 }}
          locale={{ emptyText: <Empty description={t('No hay items cargados')} /> }}>
          <Table.Column
            width={240}
            title={t('Elemento')}
            render={(_: any, item: ServiceOrderItem) => item.name ?? '-'}
          />
          <Table.Column
            width={130}
            title={
              <Tooltip title={t('Cantidad utilizada en el servicio realizado')}>
                {t('Cantidad utilizada')}
                <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
              </Tooltip>
            }
            render={(_: any, item: ServiceOrderItem) => item.used_amount ?? '-'}
            onCell={record => ({
              className: record.used_amount > 0 ? 'UsedItem' : 'NonUsedItem',
            })}
          />
          <Table.Column
            width={130}
            title={t('Cant. enviada')}
            render={(_: any, item: ServiceOrderItem) =>
              (item.package_item as PackageItem)?.execute_amount ?? '-'
            }
          />
          <Table.Column
            width={130}
            title={
              <Tooltip title={t('Cantidad que el técnico mantendra en su stock personal')}>
                {t('Cantidad para stock técnico')}
                <QuestionCircleOutlined style={{ marginLeft: '4px' }} />
              </Tooltip>
            }
            render={(_: any, item: ServiceOrderItem) => item.stock_technician_amount ?? '-'}
          />
          <Table.Column
            width={130}
            title={
              <Tooltip
                title={t('Cantidad del producto retirado del cliente que se decidió desechar')}>
                {t('Cantidad descartada')}
                <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
              </Tooltip>
            }
            render={(_: any, item: ServiceOrderItem) => item.discarded_amount ?? '-'}
          />
          <Table.Column
            width={130}
            title={
              <Tooltip title={t('Cantidad del producto que será devuelto a Acronex')}>
                {t('Cantidad a devolver')}
                <QuestionCircleOutlined style={{ marginLeft: '4px', marginTop: '4px' }} />
              </Tooltip>
            }
            render={(_: any, item: ServiceOrderItem) => item.to_return_amount ?? '-'}
          />
          <Table.Column
            width={140}
            title={t('Número de serie')}
            render={(_: any, item: ServiceOrderItem) =>
              item.product_type == PRODUCT_TYPES.PRODUCT
                ? '-'
                : (item.serial_number ?? t('No indicado'))
            }
          />
          <Table.Column
            width={130}
            title={t('Precio')}
            render={(_: any, item: ServiceOrderItem) => {
              const product = (item.package_item as PackageItem)?.product;

              if (product && typeof product === 'object' && product.symbol && product.price) {
                return `${product.symbol} ${product.price}`;
              }

              return 'N/A';
            }}
          />
          <Table.Column
            width={150}
            title={t('Precio con impuestos')}
            render={(_: any, item: ServiceOrderItem) => {
              const product = (item.package_item as PackageItem)?.product;

              if (
                product &&
                typeof product === 'object' &&
                product.symbol &&
                product.price_with_taxes
              ) {
                return `${product.symbol} ${product.price_with_taxes}`;
              }

              return 'N/A';
            }}
          />
          <Table.Column
            width={140}
            title={t('Origen')}
            render={(_: any, item: ServiceOrderItem) => {
              const originLabel = item.package_item ? t('Envío') : t('Stock técnico');

              return (
                <Tag
                  className="serviceOrderLoadedItemsTag"
                  color={item.package_item ? 'blue' : 'default'}>
                  {originLabel}
                </Tag>
              );
            }}
          />

          <Table.Column
            title={t('Observaciones')}
            width={210}
            render={(_: any, item: ServiceOrderItem) => {
              return (
                <Flex gap="4px">
                  {item.resolution_status === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_PERITAGE && (
                    <Tag className="serviceOrderLoadedItemsTag" color="blue">
                      {t('Sujeto a peritaje')}
                    </Tag>
                  )}
                  {item.in_warranty && (
                    <Tag className="serviceOrderLoadedItemsTag" color="green">
                      {t('En garantía')}
                    </Tag>
                  )}
                  {item.resolution_status === SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_TO_BILL && (
                    <Tag className="serviceOrderLoadedItemsTag" color="red">
                      {t('A Facturar')}
                    </Tag>
                  )}
                  {item.resolution_status ===
                    SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_BONUS_APPLIED && (
                    <Tag className="serviceOrderLoadedItemsTag" color="cyan">
                      {t('Bonificado')}
                    </Tag>
                  )}
                  {item.resolution_status ===
                    SERVICE_ORDER_ITEM_RESOLUTION.RESOLUTION_AUTOMATIC && (
                    <Tag className="serviceOrderLoadedItemsTag" color="purple">
                      {t('Resolución Automática')}
                    </Tag>
                  )}

                  {item.package_item != undefined &&
                    typeof item.package_item != 'number' &&
                    (item.package_item.item_shipped_reference ==
                      SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO ||
                      item.package_item.item_shipped_reference ==
                        SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION) && (
                      <Tag className="serviceOrderLoadedItemsTag" color="purple">
                        {t(ShippingItemReferenceString[item.package_item.item_shipped_reference])}
                      </Tag>
                    )}
                  {item.package_item != undefined &&
                    typeof item.package_item != 'number' &&
                    item.package_item.item_shipped_reference ==
                      SHIPPING_ITEM_REFERENCE.PREVENTIVO && (
                      <Tag className="serviceOrderLoadedItemsTag" color="orange">
                        {t('Preventivo')}
                      </Tag>
                    )}
                </Flex>
              );
            }}
          />
        </Table>
      ) : (
        <Skeleton active />
      )}
    </div>
  );
};

export default ServiceOrderLoadedItems;
